import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { Spin } from "@pankod/refine-antd";
import axios from "axios";
import { authProvider } from "authProvider";
import { HttpError } from "@pankod/refine-core";

const axiosInstance = axios.create();

// Mode TV sans login : /tv?k=<token statique client-credentials> (même mécanisme que
// CleanFlow, cf ClientCredentialPermission côté backapi). Pas de session, pas de refresh.
const TV_KEY = new URLSearchParams(window.location.search).get("k");

axiosInstance.interceptors.request.use((config) => {
  config.headers = {
    Authorization: `Bearer ${TV_KEY || localStorage.getItem("ACCESS_TOKEN")}`,
    Accept: "application/json; charset=utf-8",
    "Content-Type": "application/json; charset=utf-8",
  };
  config.timeout = 300000;
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !TV_KEY) {
      originalRequest._retry = true;
      await authProvider.refresh({});
      return axiosInstance(originalRequest);
    }
    return Promise.reject(error);
  }
);

// --- CONFIG ---

const DEFAULT_CHECKOUT = "12:00";
const DEFAULT_CHECKIN = "17:00";

const REFRESH_INTERVAL_MS = 60000; // 1 minute
// Reload complet de la page : contre le throttling des timers du navigateur de la TV
// (page figée des heures) et les tokens de session expirés silencieusement.
const PAGE_RELOAD_MS = 30 * 60 * 1000; // 30 minutes

interface TableConfig {
  key: string;
  title: string;
  suites: string[];
}

const TABLES: TableConfig[] = [
  {
    key: "Parsemées",
    title: "Parsemées",
    suites: [
      "Nova", "Origine", "Hyggelig", "Bellecour", "Opale", "Jungle",
      "Balan\u00e7oire", "Sienna", "Entre soi", "Confidence", "Cachette",
      "Eden", "Pr\u00e9cieuse", "Clotilde", "Monsieur", "Nuances",
      "Esmeralda", "Op\u00e9ra", "Scala", "Masala",
    ],
  },
  {
    key: "Pérouges",
    title: "Pérouges",
    suites: ["Duchesse", "M\u00e9daillon", "Acanthe", "Lys", "Arabesque", "Louise"],
  },
  {
    key: "Gailleton",
    title: "Gailleton",
    suites: [
      "Rendez-vous", "Fauve", "Envol", "Vertigo", "Myst\u00e8re",
      "Copper", "Paradis", "Merveille", "Blush", "James",
    ],
  },
  {
    key: "54 bis Sala (1)",
    title: "54 bis Sala",
    suites: ["Topaze", "Onyx", "Agate", "Nacre", "Rubis"],
  },
  {
    key: "54 bis Sala (2)",
    title: "54 bis Sala",
    suites: ["Grenat", "Perle", "Jaspe", "Platine"],
  },
  {
    key: "Tour Rose",
    title: "Tour Rose",
    suites: [
      "Ocre", "Eb\u00e8ne", "Terracotta", "Acquaverde", "Mia Stanza",
      "Ambre", "Alba", "Volatili", "Bianca", "Colletore",
      "Palazzo", "Amarante", "Palma", "Cobalt", "Maison Rose",
    ],
  },
  {
    key: "Gourguillon",
    title: "Gourguillon",
    suites: ["Divine", "Celeste", "Satin", "Luna", "Velours", "Stella", "Aura", "Osmose"],
  },
  {
    key: "Cuvier",
    title: "Cuvier",
    suites: ["Dona", "Marlow", "Villari", "N\u00e9roli", "Palladium", "Rom\u00e9o"],
  },
  {
    key: "Chambéry",
    title: "Chambéry",
    suites: ["Berlingot", "Confiture", "Madeleine", "Verveine", "Porcelaine"],
  },
  {
    key: "Cannes",
    title: "Cannes",
    suites: ["After Sun", "Golden Hour", "Sunset"],
  },
];

// --- TYPES ---

interface CleaningEntry {
  id: number;
  apartment_name: string;
  service_type: "FCL" | "DND" | "LCL" | null;
  checkout: string | null;
  checkin: string | null;
  notes: string | null;
  pax: number | null;
  is_block: boolean;
  block_reason: string | null;
  post_menage?: boolean;
  night_price: number | null;
  yield_price?: number | null;
  last_minute?: boolean;
}

interface ExtraGroup {
  label: string;
  count: number;
}

interface SuiteRow {
  name: string;
  departure: string | null;
  arrival: string | null;
  recouche: string | null;
  pax: number | null;
  extras: ExtraGroup[] | null;
  isBlock: boolean;
  blockReason: string | null;
  yieldPrice: number | null;
  postMenage?: boolean;
}

// --- EXTRAS ABBREVIATIONS ---

const EXTRAS_ABBREV: Record<string, string | null> = {
  "Petit-Déjeuner": "PDJ",
  "Petit-déjeuner": "PDJ",
  "Late Check Out": "Late",
  "Late check out": "Late",
  "Early Check In": "Early",
  "Early check in": "Early",
  "Ménage en cours de séjour": "Ménage",
  "Demi Bouteille de Champagne Ruinart Brut": null,
  "Bouteille de Champagne": null,
  "Massage énergétique en Suite par Chand Satya": "Massage",
  "Petit-déjeuner Luna 2 personnes": "2 PDJ",
  "Petit-déjeuner Luna 1 personne": "1 PDJ",
  "Extra animal de compagnie": null,
  "Petit dej Chambéry 2 personnes": "2 PDJ",
  "Petit dej Chambéry 1 personne": "1 PDJ",
  "Visite guidée Pérouges": "Visite Pérouges",
  "Petit déjeuner Pérouges - 2 personnes": "2 PDJ",
  "Petit déjeuner Pérouges - 1 personne": "1 PDJ",
  "PDJ Business": "PDJ Business",
  "Brunch 1 personne": "1 Brunch",
  "Brunch 2 personnes": "2 Brunch",
  "Petit déjeuner été 1 personne": "1 PDJ",
  "Petit déjeuner été 2 personnes": "1 PDJ",
  "forfait shooting": "Shooting",
  "Pralines à croquer": "Pralines",
  "Grande brioche aux pépites de chocolat": "Brioche",
  "Grande praluline 600g": "Praluline",
  "Dormir à la belle étoile": "Belle étoile",
  "Candle Light": "Candle",
  "Supplément Navette gare en Van de NUIT": "Navette",
  "Supplément Navette aéroport – Van NUIT": "Navette",
  "Supplément Navette aéroport – Van - jour": "Navette",
  "Supplément Navette gare – Van - jour": "Navette",
  "Supplément Navette aéroport – Majoration prix": "Navette",
  "Supplément Navette gare – Majoration prix": "Navette",
  "Soin visage ou massage - 1 personne": "Massage",
  "Soin visage ou massage pour deux": "Massage",
  "Lit bébé": "Lit bébé",
  "1 Lit Supp": "Lit Supp",
  "Gouter Annniversaire": "Anniv",
  "Dîner de la Saint-Valentin": null,
  "Lyon City Card 48H": "City Card",
  "Lyon City Card 24H": "City Card",
  "Petit-Déjeuner livré 1 personne": "1 PDJ",
  "Petit Déjeuner livré 2 personnes": "2 PDJ",
  "2 heures au SPA": "SPA",
  "Parking privé 58 Rue Sala": "Parking",
  "MIAM, le pass gourmand by Praline et Rosette": null,
  "Lit supplémentaire": "Lit supp",
  "Early Check In + Late Check Out": "Early + Late",
  "Repas livré dans votre Suite": "Repas livrée",
  "Lait ou chocolat au lait": null,
  "Repas Dealer de Cook par personne": null,
  "Brunch sucré/salé 5 personnes": null,
  "Bouquet Saint-Valentin": null,
  "Repas Saint-Valentin pour 2 personnes": null,
  "Prestations diverses": null,
  "Dîner & Brunch pour 2 personnes": "2 Brunch & Diner",
  "Repas de fête": "Repas livrée",
  "Visite gastronomique de Lyon Very Gone Trip": null,
  "LoveCan": "LoveCan",
  "Energy balls": null,
  "Plateau repas by Les Apothicaires": null,
  "Acaï Bowl pour 2 personnes": null,
  "Désactivé - Acaï Bowl": null,
  "Cocktail Signature X Maison Alphonse": null,
  "Plateau repas by Café Arsène": "Plateau Repas",
  "LoveBox": "LoveBox",
  "KissMi": "KissMi",
  "Des pétales...": "Pétales",
  "Location de vélo": "Vélo",
  "Mise en beauté - Maquillage et coiffure": "Maquillage & Coiffure",
  "Mise en beauté - Coiffure": "Coiffure",
  "Mise en beauté - Maquillage": "Maquillage",
  "Massage dans la Suite": "Massage",
  "désactivé - Repas livré dans votre Suite": null,
  "Désactivé - Styliste Personnelle": null,
  "Planche Apéritive pour 2": "2 Planches",
  "Un bouquet de fleur": "Bouquet",
  "100 Ballons": "100 Ballons",
  "Check out tardif": null,
  "Désactivé - Cours de Yoga dans votre Suite": null,
  "BusinesSuite offre pour les pros": null,
  "Des ballons...": "Ballons",
  "Petit-dej pour 2": "2 PDJ",
  "Chauffeur vers ou depuis les gares en Tesla": "Tesla",
  "Chauffeur depuis ou vers l aéroport en Tesla": "Tesla",
  "On cuisine ?": "Pack Cuisine",
  "Séance de sport": "Sport",
  "Expérience détente - SPA Lyon Plage": "SPA",
  "Parking privé pour Bellecour ou Hyggelig": "Parking",
  "Des plumes...": "Plumes",
  "Des macarons....": "Macarons",
  "Pack LoveMi": "LoveMi",
  "Coffret de chocolats Pralus": "Pralus",
  "Petit-Dej pour 1 personne": "1 PDJ",
};

function abbreviateExtras(notes: string | null): string | null {
  if (!notes) return null;
  const parts = notes.split(" / ");
  const abbreviated: string[] = [];
  for (const part of parts) {
    const trimmed = part.trim();
    // Check exact match first
    if (trimmed in EXTRAS_ABBREV) {
      const abbr = EXTRAS_ABBREV[trimmed];
      if (abbr !== null) abbreviated.push(abbr);
      continue;
    }
    // Fallback: case-insensitive match
    const lowerTrimmed = trimmed.toLowerCase();
    let found = false;
    for (const [key, val] of Object.entries(EXTRAS_ABBREV)) {
      if (key.toLowerCase() === lowerTrimmed) {
        if (val !== null) abbreviated.push(val);
        found = true;
        break;
      }
    }
    // If not in mapping at all, show as-is
    if (!found) abbreviated.push(trimmed);
  }
  return abbreviated.length > 0 ? abbreviated.join(" / ") : null;
}

// Regroupe les extras identiques ("Petit-Déjeuner / Petit-Déjeuner" → Petit-Déjeuner x2).
// Pour le petit-déj, le compte est plafonné au nb de pax : les doublons de saisie et les
// lignes auto "1 par matin" donnent sinon des x4 fantaisistes (vécu Mia Stanza 25/07).
function groupExtras(notes: string | null, pax: number | null): ExtraGroup[] | null {
  const abbreviated = abbreviateExtras(notes);
  if (!abbreviated) return null;
  const groups: ExtraGroup[] = [];
  for (const part of abbreviated.split(" / ")) {
    const existing = groups.find((g) => g.label === part);
    if (existing) existing.count += 1;
    else groups.push({ label: part, count: 1 });
  }
  for (const g of groups) {
    if (pax && pax > 0 && /pdj|d[ée]j/i.test(g.label) && g.count > pax) {
      g.count = pax;
    }
  }
  return groups.length > 0 ? groups : null;
}

// --- HELPERS ---

function formatHour(time: string | null, defaultTime: string): string | null {
  if (!time) return null;
  if (time === defaultTime) return "\u2715";
  const [h, m] = time.split(":");
  const hour = parseInt(h, 10);
  const min = parseInt(m, 10);
  return min > 0 ? `${hour}H${m}` : `${hour}H`;
}

// Un block m\u00e9nage (ex: "MENAGE NUIT") n'est pas une arriv\u00e9e client : la suite
// est bloqu\u00e9e pour m\u00e9nage et se lib\u00e8re (d\u00e9part) \u00e0 l'heure du checkin.
function isCleaningBlock(blockReason: string | null): boolean {
  if (!blockReason) return false;
  const normalized = blockReason
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return normalized.includes("menage");
}

function findCleaningsForSuite(
  name: string,
  cleanings: CleaningEntry[]
): CleaningEntry[] {
  const lower = name.toLowerCase();
  // Use word boundary matching to avoid "ambre" matching "Chambre"
  const regex = new RegExp(`\\b${lower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
  return cleanings.filter((c) => regex.test(c.apartment_name));
}

function buildSuiteRows(
  suites: string[],
  cleanings: CleaningEntry[]
): SuiteRow[] {
  return suites.map((name) => {
    const entries = findCleaningsForSuite(name, cleanings);

    let departure: string | null = null;
    let arrival: string | null = null;
    let recouche: string | null = null;
    let pax: number | null = null;
    let rawNotes: string | null = null;
    let isBlock = false;
    let blockReason: string | null = null;
    let yieldPrice: number | null = null;
    let postMenage = false;

    for (const entry of entries) {
      if (entry.post_menage) postMenage = true;
      if (entry.is_block) {
        isBlock = true;
        blockReason = entry.block_reason || null;
      }
      if (entry.yield_price) {
        yieldPrice = entry.yield_price;
      }
      const cleaningBlock = entry.is_block && isCleaningBlock(entry.block_reason);
      if (entry.service_type === "FCL") {
        if (cleaningBlock) {
          // Block ménage nuit : la suite se libère à 12h → départ, pas arrivée.
          departure = formatHour(entry.checkin, DEFAULT_CHECKOUT);
        } else {
          if (!isBlock) {
            departure = formatHour(entry.checkout, DEFAULT_CHECKOUT);
          }
          if (entry.checkin) {
            arrival = formatHour(entry.checkin, DEFAULT_CHECKIN);
          }
        }
        if (entry.pax) pax = entry.pax;
        if (entry.notes && !entry.is_block) {
          // les blocks portent leur note dans notes ET block_reason → on ignore
          // notes ici sinon la raison s'affiche en double ("CLIM HS / CLIM HS")
          rawNotes = entry.notes;
        }
      } else if (entry.service_type === "DND") {
        if (cleaningBlock) {
          departure = formatHour(entry.checkin, DEFAULT_CHECKOUT);
        } else {
          arrival = formatHour(entry.checkin, DEFAULT_CHECKIN);
        }
        if (entry.pax) pax = entry.pax;
        if (entry.notes && !entry.is_block) {
          rawNotes = entry.notes;
        }
      } else if (entry.service_type === "LCL") {
        recouche = "\u2715";
        if (entry.pax) pax = entry.pax;
      }
    }

    return {
      name, departure, arrival, recouche, pax,
      extras: groupExtras(rawNotes, pax),
      isBlock, blockReason, yieldPrice, postMenage,
    };
  });
}

// --- RENDU (refonte TV 09/2026) ---
// Contraintes : navigateur de TV ancien (pas d'emoji, pas de color-mix, pas de gap sur flex),
// mode clair uniquement, TOUT doit tenir à l'écran : les hauteurs de ligne et l'échelle de
// police sont calculées après rendu pour que rien ne soit coupé (extras sur 2 lignes si besoin).

interface CleaningAgentInfo {
  agent_name: string | null;
  status: string | null;
  cleaning_type: string | null;
  scheduled_start?: string | null; // "HH:mm" prévu dans CleanFlow
  start_after?: string | null;
  deadline?: string | null;
  slot_start?: string | null; // créneau tel qu'envoyé à l'agent par SMS (chaîne 12h + durées)
  slot_end?: string | null;
}

interface MemberInfo {
  id: number;
  name: string;
  color: string;
}

/** Clé de rapprochement d'une suite, insensible à la casse et aux accents.
 *  Les noms du tableau ("Rendez-vous", "Entre soi") et ceux de la base / CleanFlow
 *  ("Rendez-Vous", "Entre Soi") diffèrent parfois par une majuscule : sans ça, l'agent
 *  de ménage ne s'affichait pas sur ces suites (constaté le 18/09/2026). */
function suiteKey(name: string): string {
  const base = typeof name.normalize === "function" ? name.normalize("NFD") : name;
  return base.replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
}

/** Prénom seul pour la colonne ménage ("Kristjana MIH" -> "Kristjana"). */
function agentFirstName(name: string | null): string {
  if (!name) return "";
  return name.trim().split(/\s+/)[0];
}

/** "#3b82f6" -> "rgba(59,130,246,0.16)" (la TV ne connaît pas color-mix). */
function tint(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) return "transparent";
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})`;
}

// Répartition des zones sur N colonnes (poids = nb de suites + en-tête), la plus lourde d'abord,
// puis remise dans l'ordre de TABLES à l'intérieur de chaque colonne.
function distributeTables(tables: TableConfig[], n: number): TableConfig[][] {
  const cols = Array.from({ length: n }, () => ({ w: 0, items: [] as TableConfig[] }));
  const HEAD = 2.1;
  [...tables].sort((x, y) => y.suites.length - x.suites.length).forEach((t) => {
    const c = cols.reduce((m, x) => (x.w < m.w ? x : m));
    c.items.push(t);
    c.w += t.suites.length + HEAD;
  });
  return cols.map((c) => c.items.sort((x, y) => tables.indexOf(x) - tables.indexOf(y)));
}

const LB_CSS = `
.lb-root{position:fixed;top:0;left:0;right:0;bottom:0;background:#eef0ee;color:#161a2b;font-family:"IBM Plex Sans","Segoe UI",Roboto,Arial,sans-serif;display:flex;flex-direction:column;padding:0.5vh 0.5vw;box-sizing:border-box;overflow:hidden;z-index:9999;--s:1}
.lb-top{display:flex;align-items:center;background:#1b1f36;color:#f6f7fb;border-radius:0.5vh;padding:0.4vh 1vw;min-height:5.2vh;flex-shrink:0;margin-top:0.5vh;overflow:hidden}
.lb-top > *{margin-right:1.2vw}
.lb-date{font-family:"Barlow Condensed","Arial Narrow",Arial,sans-serif;font-weight:800;font-size:2.6vh;letter-spacing:0.04em;text-transform:uppercase;white-space:nowrap;flex-shrink:0}
.lb-clock{font-family:"Barlow Condensed","Arial Narrow",Arial,sans-serif;font-weight:600;font-size:2.4vh;opacity:.85}
.lb-sep{width:1px;height:3.2vh;background:rgba(255,255,255,.22)}
.lb-stat{white-space:nowrap;flex-shrink:0}
.lb-stat b{font-family:"Barlow Condensed","Arial Narrow",Arial,sans-serif;font-weight:800;font-size:3vh;line-height:1;margin-right:0.35vw}
.lb-stat span{font-size:1.3vh;font-weight:600;letter-spacing:.08em;text-transform:uppercase;opacity:.7}
.lb-grow{flex:1}
.lb-legend{display:block;flex:1 1 0;min-width:0;margin-left:1.2vw;margin-right:0;line-height:1.1;text-align:left}
.lb-legend .scroller{display:block;width:100%;overflow:hidden;white-space:nowrap;text-align:left}
.lb-legend .track{display:inline-block;white-space:nowrap;will-change:transform}
.lb-legend .lbl{display:block;font-size:1.1vh;letter-spacing:.1em;text-transform:uppercase;opacity:.6;margin-bottom:.2vh}
.lb-legend .who{display:inline-block;font-family:"Barlow Condensed","Arial Narrow",Arial,sans-serif;font-weight:700;font-size:1.7vh;margin-right:.9vw;white-space:nowrap;vertical-align:middle}
.lb-legend .who i{display:inline-block;width:1.5vh;height:1.5vh;border-radius:50%;margin-right:.35vw;vertical-align:middle}
.lb-alert{flex-shrink:0;margin-bottom:0.5vh;background:#c81e3a;color:#fff;border-radius:0.5vh;padding:0.5vh 1vw;font-size:2vh;font-weight:800;display:flex;align-items:center}
.lb-alert svg{width:2.4vh;height:2.4vh;margin-right:.6vw;flex-shrink:0}
.lb-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));grid-column-gap:0.5vw}
.lb-col{min-height:0;overflow:hidden}
.lb-zone{background:#fff;border-radius:0.5vh;overflow:hidden;border:1px solid #dfe2e0;margin-bottom:0.5vh}
.lb-zone h2{margin:0;background:#1b1f36;color:#f6f7fb;font-family:"Barlow Condensed","Arial Narrow",Arial,sans-serif;font-weight:800;font-size:calc(2.2vh * var(--s));letter-spacing:.1em;text-transform:uppercase;text-align:center;padding:0.3vh 0;line-height:1.25}
.lb-hdr,.lb-row{display:grid;grid-template-columns:minmax(max-content,1.2fr) 0.6fr 0.6fr 0.38fr minmax(0,1fr) minmax(0,2fr);align-items:center;grid-column-gap:0.25vw;padding:0 0.45vw}
.lb-hdr{font-size:calc(1.25vh * var(--s));font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#4b5060;border-bottom:2px solid #1b1f36;height:calc(2.4vh * var(--s))}
.lb-hdr span:nth-child(2),.lb-hdr span:nth-child(3),.lb-hdr span:nth-child(4){text-align:center}
.lb-row{border-bottom:1px solid #e3e6e4;border-left:0.35vw solid transparent;min-height:var(--rowh,3.4vh);font-size:calc(1.75vh * var(--s));line-height:1.15}
.lb-row:last-child{border-bottom:0}
.lb-row.free{background-image:repeating-linear-gradient(-45deg,transparent 0 5px,rgba(161,98,7,.14) 5px 10px)}
.lb-name{font-weight:700;white-space:nowrap;min-width:0}
.lb-row.block{background-color:rgba(200,30,58,.06);background-image:repeating-linear-gradient(-45deg,transparent 0 6px,rgba(200,30,58,.22) 6px 12px)}
.lb-row.block .lb-name{color:#c81e3a;text-decoration:line-through}
.lb-row.block .lb-name .blk{font-size:calc(1.2vh * var(--s));margin-left:.3vw;text-decoration:none;font-weight:800}
.lb-chip.price{color:#a16207;background:#fef3c7}
.lb-name .post{display:inline-block;width:calc(1.6vh * var(--s));height:calc(1.6vh * var(--s));vertical-align:middle;margin-left:.3vw}
.lb-mv{text-align:center;font-weight:800;white-space:nowrap}
.lb-mv.hour{color:#b45a00}
.lb-mv.def{font-weight:900}
.lb-mv.dep.def{color:#c81e3a}.lb-mv.arr.def{color:#1c8a4a}
.lb-stay{grid-column:2 / 4;text-align:center;font-size:calc(1.35vh * var(--s));font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#2a5db0;background:#e3ebf9;border-radius:1vh;padding:.15vh 0}
.lb-pax{text-align:center;font-weight:700;color:#4b5060}
.lb-pax.big{color:#1c8a4a}
.lb-agent{min-width:0;white-space:normal;line-height:1.1;font-size:calc(1.55vh * var(--s))}
.lb-agent .nm{font-weight:700;color:#0b6b62}
.lb-agent .at{font-weight:600;color:#2f6fb5;font-size:calc(1.35vh * var(--s));margin-left:.35vw;white-space:nowrap}
.lb-extras{min-width:0;display:flex;flex-wrap:wrap;align-items:center;padding:0.2vh 0}
.lb-chip{display:inline-block;font-size:calc(1.35vh * var(--s));font-weight:700;color:#5b21b6;background:#ede9fe;border-radius:1vh;padding:.1vh .45vw;margin:.15vh .25vw .15vh 0;white-space:normal;line-height:1.2}
.lb-chip.blk{color:#c81e3a;background:#fbe3e7}
.lb-chip.note{color:#4b5060;background:#e6e8ec}
`;

const LB_FONTS = "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap";

const SuiteZone: React.FC<{
  config: TableConfig;
  rows: SuiteRow[];
  suiteAssignments: Record<string, { color: string }>;
  suiteNotes: Record<string, string>;
  cleaningAgents: Record<string, CleaningAgentInfo>;
}> = ({ config, rows, suiteAssignments, suiteNotes, cleaningAgents }) => {
  const mv = (v: string | null, cls: string) => {
    if (!v) return <span className="lb-mv" />;
    if (v === "✕") return <span className={`lb-mv ${cls} def`}>{v}</span>;
    return <span className={`lb-mv ${cls} hour`}>{v}</span>;
  };
  return (
    <section className="lb-zone">
      <h2>{config.title}</h2>
      <div className="lb-hdr"><span>Suite</span><span>D</span><span>A</span><span>Pax</span><span>Ménage</span><span>Extras</span></div>
      {rows.map((row) => {
        const who = suiteAssignments[row.name]?.color;
        const ag = cleaningAgents[suiteKey(row.name)];
        // Horaire volontairement non affiché (décision équipe 16/09) : le prénom suffit.
        const note = suiteNotes[row.name];
        const stayOnly = !!row.recouche && !row.departure && !row.arrival;
        const style: React.CSSProperties = who && !row.isBlock ? { backgroundColor: tint(who, 0.16), borderLeftColor: who } : who ? { borderLeftColor: who } : {};
        return (
          <div key={row.name} className={`lb-row${row.yieldPrice ? " free" : ""}${row.isBlock ? " block" : ""}`} style={style}>
            <span className="lb-name" title={row.name}>
              {row.name}
              {row.isBlock ? <span className="blk">BLOCK</span> : null}
              {row.postMenage ? (
                <svg className="post" viewBox="0 0 24 24"><title>Block ménage hier — contrôler la suite</title>
                  <path fill="#22c55e" d="M19.36,2.72L20.78,4.14L15.06,9.85C16.13,11.39 16.28,13.24 15.38,14.44L9.06,8.12C10.26,7.22 12.11,7.37 13.65,8.44L19.36,2.72M5.93,17.57C3.92,15.56 2.69,13.16 2.35,10.92L7.23,8.83L14.67,16.27L12.58,21.15C10.34,20.81 7.94,19.58 5.93,17.57Z" />
                </svg>
              ) : null}
            </span>
            {stayOnly ? <span className="lb-stay">Recouche</span> : <>{mv(row.departure, "dep")}{mv(row.arrival, "arr")}</>}
            <span className={`lb-pax${row.pax && row.pax > 2 ? " big" : ""}`}>{row.pax ?? ""}</span>
            <span className="lb-agent" title={ag?.agent_name || ""}>
              {ag && ag.agent_name ? <span className="nm">{agentFirstName(ag.agent_name)}</span> : null}
            </span>
            <span className="lb-extras">
              {row.yieldPrice ? <span className="lb-chip price">{Math.round(row.yieldPrice)} € libre</span> : null}
              {row.blockReason ? <span className="lb-chip blk">{row.blockReason}</span> : null}
              {(row.extras || []).map((g, i) => (
                <span key={i} className="lb-chip">{g.label}{g.count > 1 ? ` ×${g.count}` : ""}</span>
              ))}
              {note ? <span className="lb-chip note">{note}</span> : null}
            </span>
          </div>
        );
      })}
    </section>
  );
};

/** Ajuste --rowh puis --s (échelle de police) pour que chaque colonne tienne sans défilement. */
function fitBoard(root: HTMLElement) {
  const colsEl = root.querySelector<HTMLElement>(".lb-cols");
  if (!colsEl) return;
  const cols = Array.from(colsEl.querySelectorAll<HTMLElement>(".lb-col"));
  const H = colsEl.clientHeight;
  let maxRows = 0;
  let heads = 0;
  cols.forEach((c) => {
    const rows = c.querySelectorAll(".lb-row").length;
    let h = 0;
    c.querySelectorAll<HTMLElement>(".lb-zone").forEach((z) => {
      const h2 = z.querySelector<HTMLElement>("h2");
      const hd = z.querySelector<HTMLElement>(".lb-hdr");
      h += (h2 ? h2.offsetHeight : 0) + (hd ? hd.offsetHeight : 0) + 8;
    });
    if (rows > maxRows) { maxRows = rows; heads = h; }
  });
  const rowh = Math.max(18, Math.floor((H - heads) / Math.max(1, maxRows)));
  root.style.setProperty("--rowh", `${rowh}px`);
  // Réduit l'échelle tant qu'une colonne déborde (extras sur 2 lignes, noms longs…)
  let scale = 1;
  root.style.setProperty("--s", "1");
  for (let i = 0; i < 12; i++) {
    const overflow = cols.some((c) => c.scrollHeight > c.clientHeight + 1);
    if (!overflow) break;
    scale -= 0.04;
    root.style.setProperty("--s", String(scale));
  }
  // Légende des contrôleurs : défilement continu (type bandeau) uniquement si elle dépasse.
  // Le contenu est dupliqué pour boucler sans à-coup ; vitesse constante ~30 px/s.
  const scroller = root.querySelector<HTMLElement>(".lb-legend .scroller");
  const track = scroller ? scroller.querySelector<HTMLElement>(".track") : null;
  if (scroller && track) {
    let st = document.getElementById("lb-marquee") as HTMLStyleElement | null;
    if (!st) { st = document.createElement("style"); st.id = "lb-marquee"; document.head.appendChild(st); }
    // Le clone est recréé à chaque passage (les noms peuvent changer à chaque rafraîchissement)
    scroller.querySelectorAll<HTMLElement>(".track-clone").forEach((c) => c.remove());
    track.style.animation = "";
    const single = Math.max(track.scrollWidth, Math.round(track.getBoundingClientRect().width));
    const avail = Math.min(scroller.clientWidth || 1e9, Math.round(scroller.getBoundingClientRect().width) || 1e9);
    if (single > avail + 4) {
      const gap = Math.round(avail * 0.15);
      const c = track.cloneNode(true) as HTMLElement;
      c.className = "track track-clone";
      c.setAttribute("aria-hidden", "true");
      c.style.marginLeft = `${gap}px`;
      scroller.appendChild(c);
      const dist = Math.round(single + gap);
      const dur = Math.max(10, Math.round(dist / 30));
      st.textContent = `@keyframes lbmq{0%{transform:translateX(0)}100%{transform:translateX(-${dist}px)}}`;
      scroller.querySelectorAll<HTMLElement>(".track").forEach((t) => { t.style.animation = `lbmq ${dur}s linear infinite`; });
    }
  }
}

// --- NOTIFICATION TYPES ---

interface BookingNotification {
  id: number;
  booking_id: number;
  room_names: string;
  client_name: string;
  total_price: string;
  camefrom: string;
  start_date: string | null;
  end_date: string | null;
  created_at: string | null;
}

interface ToastData extends BookingNotification {
  phase: "fadein" | "visible" | "fadeout" | "done";
}

const NOTIFICATION_POLL_MS = 15000;
const TOAST_FADEIN_MS = 800;
const TOAST_VISIBLE_MS = 10000;
const TOAST_FADEOUT_MS = 800;
const TOAST_STAGGER_MS = 7000;

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const now = ctx.currentTime;

    // Soft satisfying "ding" - two harmonics with gentle decay
    [523.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = "sine";
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(i === 0 ? 0.25 : 0.15, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
      osc.start(now);
      osc.stop(now + 1.5);
    });

    // Soft high sparkle
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 1318.5;
    osc2.type = "sine";
    gain2.gain.setValueAtTime(0, now + 0.1);
    gain2.gain.linearRampToValueAtTime(0.08, now + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
    osc2.start(now + 0.1);
    osc2.stop(now + 1.2);
  } catch (e) {}
}

const CAMEFROM_LABELS: Record<string, string> = {
  DIRECT_SITE: "Site Direct",
  DIRECT_TEL: "Téléphone",
  DIRECT_APP: "App",
  BOOKING: "Booking.com",
  EXPEDIA: "Expedia",
  SPLENDIA: "Splendia",
  AIRBNB: "Airbnb",
  ABRITEL: "Abritel",
  OTHER: "Autre",
};

const CamefromIcon: React.FC<{ camefrom: string; size?: string }> = ({ camefrom, size = "3vw" }) => {
  const s = { width: size, height: size, display: "inline-block", verticalAlign: "middle" };

  switch (camefrom) {
    case "BOOKING":
      // Booking.com "B." logo
      return (
        <svg viewBox="0 0 300 300" style={s}>
          <rect width="300" height="300" rx="30" fill="#003580" />
          <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle"
            fill="#ffffff" fontSize="200" fontWeight="900" fontFamily="Arial, sans-serif">
            B.
          </text>
        </svg>
      );
    case "EXPEDIA":
      // Expedia yellow circle
      return (
        <svg viewBox="0 0 300 300" style={s}>
          <rect width="300" height="300" rx="30" fill="#FBCC33" />
          <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle"
            fill="#1C1C4A" fontSize="200" fontWeight="900" fontFamily="Arial, sans-serif">
            E
          </text>
        </svg>
      );
    case "AIRBNB":
      // Airbnb
      return (
        <svg viewBox="0 0 300 300" style={s}>
          <rect width="300" height="300" rx="30" fill="#FF5A5F" />
          <path d="M150 80c-15 0-28 8-35 20l-30 52c-5 9-8 19-8 29 0 25 18 45 40 49 8 1 16-1 23-6l10-8 10 8c7 5 15 7 23 6 22-4 40-24 40-49 0-10-3-20-8-29l-30-52c-7-12-20-20-35-20z"
            fill="#ffffff" />
        </svg>
      );
    case "ABRITEL":
      return (
        <svg viewBox="0 0 300 300" style={s}>
          <rect width="300" height="300" rx="30" fill="#0E2B5C" />
          <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle"
            fill="#ffffff" fontSize="200" fontWeight="900" fontFamily="Arial, sans-serif">
            A
          </text>
        </svg>
      );
    case "SPLENDIA":
      return (
        <svg viewBox="0 0 300 300" style={s}>
          <rect width="300" height="300" rx="30" fill="#8B6914" />
          <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle"
            fill="#ffffff" fontSize="200" fontWeight="900" fontFamily="Arial, sans-serif">
            S
          </text>
        </svg>
      );
    case "DIRECT_SITE":
      // Globe icon
      return (
        <svg viewBox="0 0 300 300" style={s}>
          <rect width="300" height="300" rx="30" fill="#1a1a2e" />
          <circle cx="150" cy="150" r="80" fill="none" stroke="#ffffff" strokeWidth="12" />
          <ellipse cx="150" cy="150" rx="40" ry="80" fill="none" stroke="#ffffff" strokeWidth="12" />
          <line x1="70" y1="150" x2="230" y2="150" stroke="#ffffff" strokeWidth="12" />
          <line x1="150" y1="70" x2="150" y2="230" stroke="#ffffff" strokeWidth="12" />
        </svg>
      );
    case "DIRECT_TEL":
      // Phone icon
      return (
        <svg viewBox="0 0 300 300" style={s}>
          <rect width="300" height="300" rx="30" fill="#1a1a2e" />
          <path d="M100 80c0-8 6-15 14-15h72c8 0 14 7 14 15v140c0 8-6 15-14 15h-72c-8 0-14-7-14-15z"
            fill="none" stroke="#ffffff" strokeWidth="14" />
          <circle cx="150" cy="205" r="12" fill="#ffffff" />
        </svg>
      );
    case "DIRECT_APP":
      // Mobile/app icon
      return (
        <svg viewBox="0 0 300 300" style={s}>
          <rect width="300" height="300" rx="30" fill="#1a1a2e" />
          <rect x="105" y="60" width="90" height="180" rx="12" fill="none" stroke="#ffffff" strokeWidth="14" />
          <circle cx="150" cy="218" r="10" fill="#ffffff" />
        </svg>
      );
    default:
      // Question mark
      return (
        <svg viewBox="0 0 300 300" style={s}>
          <rect width="300" height="300" rx="30" fill="#555555" />
          <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle"
            fill="#ffffff" fontSize="200" fontWeight="900" fontFamily="Arial, sans-serif">
            ?
          </text>
        </svg>
      );
  }
};

// --- OVERLAY NOTIFICATION ---

const OverlayNotification: React.FC<{ toast: ToastData }> = ({ toast }) => {
  const opacity = toast.phase === "fadein" ? 0 : toast.phase === "fadeout" ? 0 : 1;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10001,
        opacity: toast.phase === "visible" ? 1 : 0,
        transition: `opacity ${TOAST_FADEIN_MS}ms ease`,
      }}
    >
      <div
        style={{
          background: "linear-gradient(145deg, #0f4c2e, #166534)",
          borderRadius: "2vw",
          padding: "4vw 6vw",
          textAlign: "center",
          color: "#ffffff",
          boxShadow: "0 0 60px rgba(34, 197, 94, 0.3)",
          border: "3px solid #22c55e",
          transform: toast.phase === "visible" ? "scale(1)" : "scale(0.9)",
          transition: `transform ${TOAST_FADEIN_MS}ms ease, opacity ${TOAST_FADEIN_MS}ms ease`,
          maxWidth: "70vw",
        }}
      >
        <div
          style={{
            fontSize: "1.5vw",
            fontWeight: 600,
            color: "#86efac",
            letterSpacing: "0.3vw",
            textTransform: "uppercase",
            marginBottom: "2vw",
          }}
        >
          Nouvelle Réservation
        </div>
        <div
          style={{
            fontSize: "2.5vw",
            fontWeight: 900,
            marginBottom: "1.5vw",
            lineHeight: 1.2,
          }}
        >
          {toast.room_names}
        </div>
        <div
          style={{
            fontSize: "1.8vw",
            fontWeight: 600,
            marginBottom: "1vw",
          }}
        >
          {toast.client_name}
        </div>
        <div
          style={{
            marginBottom: "0.8vw",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.8vw",
          }}
        >
          <CamefromIcon camefrom={toast.camefrom} size="3vw" />
          <span style={{ fontSize: "1.4vw", opacity: 0.8 }}>
            {CAMEFROM_LABELS[toast.camefrom] || toast.camefrom}
          </span>
        </div>
        {toast.start_date && toast.end_date && (
          <div
            style={{
              fontSize: "1.4vw",
              opacity: 0.8,
              marginBottom: "1.5vw",
            }}
          >
            {toast.start_date} → {toast.end_date}
          </div>
        )}
        <div
          style={{
            fontSize: "4vw",
            fontWeight: 900,
            color: "#4ade80",
            textShadow: "0 0 30px rgba(74, 222, 128, 0.4)",
            marginTop: "0.5vw",
          }}
        >
          {toast.total_price} €
        </div>
      </div>
    </div>
  );
};

// --- MAIN ---

export const LiveBoardList: React.FC<IResourceComponentsProps> = () => {
  const [cleanings, setCleanings] = useState<CleaningEntry[]>([]);
  const [assignments, setAssignments] = useState<Record<string, { color: string }>>({});
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [suiteNotes, setSuiteNotes] = useState<Record<string, string>>({});
  const [cleaningAgents, setCleaningAgents] = useState<Record<string, CleaningAgentInfo>>({});
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [clock, setClock] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState("");
  const lastNotifIdRef = useRef<number>(0);
  const initDoneRef = useRef<boolean>(false);

  const fetchData = async () => {
    try {
      // Date LOCALE (pas toISOString/UTC : entre minuit et 2h le board affichait la veille)
      const now = new Date();
      const today = `${now.getFullYear()}-${("0" + (now.getMonth() + 1)).slice(-2)}-${("0" + now.getDate()).slice(-2)}`;
      setCurrentDate(today);
      const apiUrl = process.env?.REACT_APP_API_URL || "";
      const [cleaningsRes, assignmentsRes, membersRes, notesRes, agentsRes] = await Promise.all([
        axiosInstance.get(`${apiUrl}/housekeeping?date=${today}`),
        axiosInstance.get(`${apiUrl}/api/liveboard-assignments?date=${today}`),
        axiosInstance.get(`${apiUrl}/api/liveboard-members`),
        axiosInstance.get(`${apiUrl}/api/liveboard-notes?date=${today}`),
        // Agents de ménage affectés dans CleanFlow (via backapi). Jamais bloquant.
        axiosInstance.get(`${apiUrl}/api/liveboard-cleaning-agents?date=${today}`).catch(() => ({ data: { data: {} } })),
      ]);
      setCleanings(cleaningsRes.data?.data?.cleanings || []);
      setAssignments(assignmentsRes.data?.data || {});
      setMembers(membersRes.data?.data || []);
      setSuiteNotes(notesRes.data?.data || {});
      // Ré-indexé sur la clé normalisée : voir suiteKey().
      const rawAgents: Record<string, CleaningAgentInfo> = agentsRes.data?.data || {};
      const agentsByKey: Record<string, CleaningAgentInfo> = {};
      Object.keys(rawAgents).forEach((k) => { agentsByKey[suiteKey(k)] = rawAgents[k]; });
      setCleaningAgents(agentsByKey);
    } catch (err) {
      console.error("LiveBoard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (notif: BookingNotification) => {
    const toastData: ToastData = { ...notif, phase: "fadein" };
    setToasts((prev) => [...prev, toastData]);

    // Fade in
    requestAnimationFrame(() => {
      setTimeout(() => {
        setToasts((prev) =>
          prev.map((t) => (t.id === notif.id ? { ...t, phase: "visible" } : t))
        );
        playNotificationSound();
      }, 50);
    });

    // Fade out
    setTimeout(() => {
      setToasts((prev) =>
        prev.map((t) => (t.id === notif.id ? { ...t, phase: "fadeout" } : t))
      );
    }, TOAST_FADEIN_MS + TOAST_VISIBLE_MS);

    // Remove
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== notif.id));
    }, TOAST_FADEIN_MS + TOAST_VISIBLE_MS + TOAST_FADEOUT_MS);
  };

  const fetchNotifications = async () => {
    if (!initDoneRef.current) return;
    try {
      const apiUrl = process.env?.REACT_APP_API_URL || "";
      const url = lastNotifIdRef.current > 0
        ? `${apiUrl}/api/liveboard-notifications?after_id=${lastNotifIdRef.current}`
        : `${apiUrl}/api/liveboard-notifications`;
      const res = await axiosInstance.get(url);
      const newNotifs: BookingNotification[] = res.data?.data || [];

      if (newNotifs.length > 0) {
        lastNotifIdRef.current = Math.max(...newNotifs.map((n) => n.id));

        newNotifs.forEach((notif, index) => {
          setTimeout(() => showToast(notif), index * TOAST_STAGGER_MS);
        });
      }
    } catch (err) {
      console.error("Notification fetch error:", err);
    }
  };

  // Initial load: fetch notifications silently to set lastNotifIdRef (no toasts)
  const initNotifications = async () => {
    try {
      const apiUrl = process.env?.REACT_APP_API_URL || "";
      const res = await axiosInstance.get(`${apiUrl}/api/liveboard-notifications`);
      const notifs: BookingNotification[] = res.data?.data || [];
      if (notifs.length > 0) {
        lastNotifIdRef.current = Math.max(...notifs.map((n) => n.id));
      }
    } catch (err) {
      console.error("Init notifications error:", err);
    }
    initDoneRef.current = true;
  };

  // Polices web (avec repli Arial si la TV ne les charge pas)
  useEffect(() => {
    if (document.querySelector(`link[href="${LB_FONTS}"]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = LB_FONTS;
    document.head.appendChild(l);
  }, []);

  // Horloge de la barre du haut
  useEffect(() => {
    const t = () => setClock(new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }));
    t();
    const id = setInterval(t, 15000);
    return () => clearInterval(id);
  }, []);

  // Tout doit tenir à l'écran : recalcul après chaque rendu de données et au redimensionnement
  useLayoutEffect(() => {
    const run = () => { if (rootRef.current) fitBoard(rootRef.current); };
    run();
    const t1 = setTimeout(run, 400);   // après chargement des polices
    const t2 = setTimeout(run, 2500);
    window.addEventListener("resize", run);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", run); };
  });

  useEffect(() => {
    fetchData();
    initNotifications();
    const dataInterval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    const notifInterval = setInterval(fetchNotifications, NOTIFICATION_POLL_MS);
    const reloadTimer = setTimeout(() => window.location.reload(), PAGE_RELOAD_MS);
    return () => {
      clearInterval(dataInterval);
      clearInterval(notifInterval);
      clearTimeout(reloadTimer);
    };
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#eef0ee" }}>
        <Spin size="large" />
      </div>
    );
  }

  // Only show members that have at least one assignment today
  const activeColors = new Set(Object.values(assignments).map((a) => a.color));
  const activeMembers = members.filter((m) => activeColors.has(m.color));

  // Stats
  const TOTAL_SUITES = 82; // 79 + 3 apparts Cannes (Georges vendue, retirée 09/2026)
  const allSuites: string[] = [];
  TABLES.forEach((t) => t.suites.forEach((s) => allSuites.push(s)));
  const stats = { departures: 0, arrivals: 0, recouches: 0, blocks: 0, ca: 0, caPotentiel: 0 };
  for (const suite of allSuites) {
    const regex = new RegExp(`\\b${suite.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    const entries = cleanings.filter((c) => regex.test(c.apartment_name));
    let hasDep = false, hasArr = false, hasRec = false, hasBlock = false;
    for (const e of entries) {
      if (e.is_block) { hasBlock = true; continue; }
      if (e.service_type === "FCL") { hasDep = true; if (e.checkin) hasArr = true; }
      else if (e.service_type === "DND") hasArr = true;
      else if (e.service_type === "LCL") hasRec = true;
      // CA nuitée: suites occupées ce soir (arrivées + recouches)
      if ((e.service_type === "DND" || e.service_type === "LCL" || (e.service_type === "FCL" && e.checkin)) && e.night_price) {
        stats.ca += e.night_price;
      }
    }
    if (hasDep) stats.departures++;
    if (hasArr) stats.arrivals++;
    if (hasRec) stats.recouches++;
    // Bloc sans arrivée = suite indisponible (pas restante, pas dans le TO)
    if (hasBlock && !hasArr && !hasRec) stats.blocks++;
    // CA potentiel: uniquement les suites vraiment libres ce soir (pas d'arrivée, pas de recouche, pas de bloc)
    if (!hasArr && !hasRec && !hasBlock) {
      for (const e of entries) {
        if (e.yield_price) stats.caPotentiel += e.yield_price;
      }
    }
  }
  // TO = suites occupées ce soir / total (on exclut les blocs du dénominateur)
  const availableSuites = TOTAL_SUITES - stats.blocks;
  const to = Math.round(((stats.arrivals + stats.recouches) / availableSuites) * 100);
  const remaining = availableSuites - stats.arrivals - stats.recouches;

  // Arrivées réservées le jour même (bandeau persistant, vs toast éphémère)
  const lastMinuteArrivals = cleanings.filter((c) => c.last_minute && c.checkin && !c.is_block);

  const columns = distributeTables(TABLES, 4);
  const rowsByKey: Record<string, SuiteRow[]> = {};
  TABLES.forEach((t) => { rowsByKey[t.key] = buildSuiteRows(t.suites, cleanings); });
  const dateLabel = (() => {
    const d = new Date(`${currentDate}T12:00:00`);
    return isNaN(d.getTime()) ? currentDate : d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  })();
  const statItems: Array<[string | number, string, string]> = [
    [stats.departures, "Départs", "#ff6b81"],
    [stats.arrivals, "Arrivées", "#4ade80"],
    [stats.recouches, "Recouches", "#93c5fd"],
    [`${to} %`, "TO", "#ffffff"],
    [remaining, "Restantes", "#fbbf24"],
    [`${Math.round(stats.ca).toLocaleString("fr-FR")} €`, "CA nuitée", "#c4b5fd"],
  ];
  if (stats.caPotentiel > 0) statItems.push([`${Math.round(stats.caPotentiel).toLocaleString("fr-FR")} €`, "Restant NANR", "#fbbf24"]);

  return (
    <>
      <style>{LB_CSS}</style>
      {toasts.map((toast) => (
        <OverlayNotification key={toast.id} toast={toast} />
      ))}
      <div className="lb-root" ref={rootRef}>
        {lastMinuteArrivals.length > 0 && (
          <div className="lb-alert">
            {/* mdi:alert (SVG inline : la TV ne rend pas les emojis) */}
            <svg viewBox="0 0 24 24"><path fill="#fff" d="M13 14h-2V9h2m0 9h-2v-2h2M1 21h22L12 2L1 21Z" /></svg>
            <span>
              {"ARRIVÉE DERNIÈRE MINUTE : "}
              {lastMinuteArrivals
                .map((a) => `${a.apartment_name}${a.pax ? ` ${a.pax} pax` : ""}${a.checkin ? ` à ${a.checkin}` : ""}`)
                .join(" • ")}
            </span>
          </div>
        )}
        <div className="lb-cols">
          {columns.map((tables, ci) => (
            <div key={ci} className="lb-col">
              {tables.map((config) => (
                <SuiteZone
                  key={config.key}
                  config={config}
                  rows={rowsByKey[config.key]}
                  suiteAssignments={assignments}
                  suiteNotes={suiteNotes}
                  cleaningAgents={cleaningAgents}
                />
              ))}
            </div>
          ))}
        </div>
        <div className="lb-top">
          <span className="lb-date">{dateLabel}</span>
          <span className="lb-clock">{clock}</span>
          <span className="lb-sep" />
          {statItems.map(([v, l, c]) => (
            <span key={l} className="lb-stat"><b style={{ color: c }}>{v}</b><span>{l}</span></span>
          ))}
          {activeMembers.length > 0 && (
            <span className="lb-legend">
              <span className="lbl">Contrôle</span>
              <span className="scroller">
                <span className="track">
                  {activeMembers.map((m) => (
                    <span key={m.id} className="who"><i style={{ background: m.color }} />{m.name}</span>
                  ))}
                </span>
              </span>
            </span>
          )}
        </div>
      </div>
    </>
  );
};
